import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import TableChartIcon from '@mui/icons-material/TableChart';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import * as XLSX from 'xlsx-js-style';
import PuffLoader from 'react-spinners/PuffLoader';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AutocompleteSelectFilter from '../../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import ReportingFollowUpService from '../../../Services/reportingFollowUp.service';
import ClientService from '../../../Services/client.service';
import { applyWorkbookRtl, buildReportSheet } from '../../../Utils/ExcelReport';
import { confirmWithholdingReporting, withholdingPeriodMonths } from '../../../Utils/withholdingReportingConfirm';
import ReportingFollowUpModal from '../ReportingFollowUpModal';
import { FollowUpDatePicker, formatAmount } from '../SubComponents/SectionParts';
import {
    FOLLOW_UP_SECTIONS,
    FOLLOW_UP_STATUS_OPTIONS,
    PAYMENT_METHOD_OPTIONS,
    REPORTING_FOLLOW_UP_SAVED_EVENT,
    getMonthName,
    reportingTypeLabel,
    toDateInputValue,
    todayDateString,
} from '../reportingFollowUpConstants';
import shared from '../ReportingFollowUp.module.scss';
import styles from './ReportingFollowUpOverview.module.scss';

const SECTION_COLUMNS = [
    { section: FOLLOW_UP_SECTIONS.VAT, labelKey: 'FollowUpVatTitle' },
    { section: FOLLOW_UP_SECTIONS.INCOME_TAX_ADVANCE, labelKey: 'FollowUpAdvanceTitle' },
    { section: FOLLOW_UP_SECTIONS.INCOME_TAX_WITHHOLDING, labelKey: 'FollowUpWithholdingTitle' },
    { section: FOLLOW_UP_SECTIONS.PERSONAL_NATIONAL_INSURANCE, labelKey: 'FollowUpPersonalNiTitle' },
    { section: FOLLOW_UP_SECTIONS.NATIONAL_INSURANCE_WITHHOLDING, labelKey: 'FollowUpNiWithholdingTitle' },
];

const ROW_FILTERS = {
    ALL: 'all',
    UNREPORTED: 'unreported',
    MATERIAL_MISSING: 'materialMissing',
};

const WITHHOLDING_CONCURRENCY = 4;

const toYearMonth = (date) => {
    const d = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

const toServerDate = (dateString) => (dateString ? `${dateString}T00:00:00` : null);

/** Server DateTime → month number (1-12), without time-zone shifts. */
const monthOf = (value) => Number(toDateInputValue(value).slice(5, 7)) || null;

const formatShortDate = (value) => {
    const date = toDateInputValue(value);
    return date ? `${date.slice(8, 10)}/${date.slice(5, 7)}` : '';
};

const sectionOf = (row, section) => row.sections?.find((s) => s.section === section) || null;

const isOpenSection = (s) => !!s && !s.notRequired && !s.isReported;

const periodLabel = (s, year) => {
    if (!s) return '';
    const start = monthOf(s.startDate);
    const end = monthOf(s.endDate);
    const pad = (m) => String(m).padStart(2, '0');
    return start === end ? `${pad(start)}/${year}` : `${pad(start)}-${pad(end)}/${year}`;
};

/** Runs `task` over `items` with at most `limit` in flight; resolves to the items whose task succeeded. */
const runLimited = async (items, limit, task) => {
    const succeeded = [];
    let next = 0;
    const worker = async () => {
        while (next < items.length) {
            const item = items[next];
            next += 1;
            try {
                await task(item);
                succeeded.push(item);
            } catch (error) {
                console.error('Reporting follow-up overview task failed:', error);
            }
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
    return succeeded;
};

/**
 * Monthly reporting tracking for many clients at once: each section's reported state and follow-up
 * fields per client, bulk updates for the selected clients, and the full per-client modal.
 */
const ReportingFollowUpOverviewModal = ({
    open,
    onClose,
    clients,
    user,
    token,
    defaultDate,
    onWithholdingReportingChange,
}) => {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();
    const language = i18n.language;
    const accountantId = user?.accountantId;

    const [{ year, month }, setYearMonth] = useState(() => toYearMonth(defaultDate));
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [search, setSearch] = useState('');
    const [rowFilter, setRowFilter] = useState(ROW_FILTERS.ALL);
    const [selected, setSelected] = useState(() => new Set());
    const [bulkSections, setBulkSections] = useState([]);
    const [bulkStatus, setBulkStatus] = useState(null);
    const [bulkPayment, setBulkPayment] = useState(null);
    const [bulkReportDate, setBulkReportDate] = useState('');
    const [bulkPaymentDate, setBulkPaymentDate] = useState('');
    const [detailClient, setDetailClient] = useState(null);
    const [openingCaseId, setOpeningCaseId] = useState(null);
    const [togglingCaseId, setTogglingCaseId] = useState(null);
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (open) setYearMonth(toYearMonth(defaultDate));
        // Only re-seed when the modal opens, not while the user browses months.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        setSelected(new Set());
    }, [open, year, month]);

    const caseIds = useMemo(
        () => [...new Set((clients || []).map((c) => c?.caseId).filter(Boolean))],
        [clients]
    );

    const load = useCallback(async () => {
        if (!open || !accountantId || !token || caseIds.length === 0) {
            setRows([]);
            return;
        }
        const requestId = ++requestIdRef.current;
        setLoading(true);
        try {
            const data = await ReportingFollowUpService.getOverview({ accountantId, year, month, caseIds }, token);
            if (requestId === requestIdRef.current) setRows(data || []);
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            console.error('Error loading reporting follow-up overview:', error);
            toast.error(t('ReportingFollowUpLoadFailed'));
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    }, [open, accountantId, token, caseIds, year, month, t]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!open) return undefined;
        const handleSaved = () => load();
        window.addEventListener(REPORTING_FOLLOW_UP_SAVED_EVENT, handleSaved);
        return () => window.removeEventListener(REPORTING_FOLLOW_UP_SAVED_EVENT, handleSaved);
    }, [open, load]);

    // ---------------------------------------------------------------- options

    const yearOptions = useMemo(() => {
        const current = new Date().getFullYear();
        return Array.from({ length: 8 }, (_, i) => current + 1 - i).map((y) => ({ value: y, label: String(y) }));
    }, []);

    const monthOptions = useMemo(
        () => Array.from({ length: 12 }, (_, i) => i + 1).map((m) => ({
            value: m,
            label: `${String(m).padStart(2, '0')} - ${getMonthName(m, language)}`,
        })),
        [language]
    );

    const filterOptions = useMemo(() => [
        { value: ROW_FILTERS.ALL, label: t('FollowUpOverviewFilterAll') },
        { value: ROW_FILTERS.UNREPORTED, label: t('FollowUpOverviewFilterUnreported') },
        { value: ROW_FILTERS.MATERIAL_MISSING, label: t('FollowUpOverviewFilterMaterialMissing') },
    ], [t]);

    const sectionOptions = useMemo(
        () => SECTION_COLUMNS.map((c) => ({ value: c.section, label: t(c.labelKey) })),
        [t]
    );
    const statusOptions = useMemo(
        () => FOLLOW_UP_STATUS_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
        [t]
    );
    const paymentOptions = useMemo(
        () => PAYMENT_METHOD_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
        [t]
    );
    const statusLabel = (value) => (value ? statusOptions.find((o) => o.value === value)?.label : '');
    const paymentLabel = (value) => (value ? paymentOptions.find((o) => o.value === value)?.label : '');

    // ---------------------------------------------------------------- rows

    const visibleRows = useMemo(() => {
        const query = search.trim().toLowerCase();
        return rows.filter((row) => {
            if (query && !`${row.name || ''} ${row.caseId}`.toLowerCase().includes(query)) return false;
            if (rowFilter === ROW_FILTERS.UNREPORTED) return row.sections?.some(isOpenSection);
            if (rowFilter === ROW_FILTERS.MATERIAL_MISSING) return !row.materialReceivedDate;
            return true;
        });
    }, [rows, search, rowFilter]);

    const summary = useMemo(() => SECTION_COLUMNS.map(({ section }) => {
        const relevant = visibleRows.map((row) => sectionOf(row, section)).filter((s) => s && !s.notRequired);
        return { section, reported: relevant.filter((s) => s.isReported).length, total: relevant.length };
    }), [visibleRows]);

    const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((row) => selected.has(row.caseId));
    const someVisibleSelected = visibleRows.some((row) => selected.has(row.caseId));

    const toggleAllVisible = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            visibleRows.forEach((row) => (allVisibleSelected ? next.delete(row.caseId) : next.add(row.caseId)));
            return next;
        });
    };

    const toggleRow = (caseId) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(caseId)) next.delete(caseId);
            else next.add(caseId);
            return next;
        });
    };

    const selectedRows = rows.filter((row) => selected.has(row.caseId));

    // ---------------------------------------------------------------- actions

    const runBulkUpdate = async (payload) => {
        setBusy(true);
        try {
            await ReportingFollowUpService.bulkUpdate({
                accountantId,
                year,
                month,
                caseIds: selectedRows.map((row) => row.caseId),
                ...payload,
            }, token);
            toast.success(t('SavedSuccessfully'));
            await load();
        } catch (error) {
            console.error('Error bulk updating reporting follow-up:', error);
            toast.error(t('ReportingFollowUpSaveFailed'));
        } finally {
            setBusy(false);
        }
    };

    const canApplyTracking = selectedRows.length > 0
        && bulkSections.length > 0
        && (bulkStatus !== null || bulkPayment !== null || !!bulkReportDate || !!bulkPaymentDate);

    const applyTracking = () => runBulkUpdate({
        sections: bulkSections.map((o) => o.value),
        followUpStatus: bulkStatus?.value ?? null,
        paymentMethod: bulkPayment?.value ?? null,
        reportDate: toServerDate(bulkReportDate),
        paymentDate: toServerDate(bulkPaymentDate),
    });

    const markMaterialReceived = () => runBulkUpdate({ materialReceivedDate: toServerDate(todayDateString()) });

    /**
     * Turns the monthly withholding reporting flag on/off through Clients/UpdateWithholdingReporting
     * for every month of each row's withholding period, and stamps the follow-up reporting date.
     */
    const setWithholdingReporting = async (targetRows, enable) => {
        const done = await runLimited(targetRows, WITHHOLDING_CONCURRENCY, async (row) => {
            const period = sectionOf(row, FOLLOW_UP_SECTIONS.INCOME_TAX_WITHHOLDING);
            const months = withholdingPeriodMonths(monthOf(period?.startDate) || month, monthOf(period?.endDate) || month);
            for (const m of months) {
                await ClientService.UpdateWithholdingReporting(token, row.caseId, year, m, enable);
            }
            onWithholdingReportingChange?.({ caseId: row.caseId, year, months, value: enable });
        });

        if (enable && done.length > 0) {
            try {
                await ReportingFollowUpService.bulkUpdate({
                    accountantId,
                    year,
                    month,
                    caseIds: done.map((row) => row.caseId),
                    sections: [FOLLOW_UP_SECTIONS.INCOME_TAX_WITHHOLDING],
                    stampReportDateIfEmpty: true,
                }, token);
            } catch (error) {
                console.error('Error stamping withholding reporting date:', error);
            }
        }

        if (done.length === targetRows.length) toast.success(t('WithholdingReportingUpdatedSuccessfully'));
        else toast.error(t('FollowUpOverviewPartialFailure', { done: done.length, total: targetRows.length }));
        await load();
    };

    const toggleRowWithholding = async (row) => {
        const period = sectionOf(row, FOLLOW_UP_SECTIONS.INCOME_TAX_WITHHOLDING);
        if (!period || togglingCaseId) return;
        const enable = !period.isReported;
        const confirmed = await confirmWithholdingReporting({
            t,
            direction: dir,
            clientName: row.name || row.caseId,
            monthLabel: periodLabel(period, year),
            enable,
        });
        if (!confirmed) return;
        setTogglingCaseId(row.caseId);
        try {
            await setWithholdingReporting([row], enable);
        } finally {
            setTogglingCaseId(null);
        }
    };

    const reportSelectedWithholding = async () => {
        const targets = selectedRows.filter((row) => !sectionOf(row, FOLLOW_UP_SECTIONS.INCOME_TAX_WITHHOLDING)?.isReported);
        if (targets.length === 0) {
            toast.info(t('FollowUpOverviewNothingToReport'));
            return;
        }
        const confirmed = await confirmWithholdingReporting({
            t,
            direction: dir,
            clientName: t('selectedClientsCount', { count: targets.length }),
            monthLabel: `${String(month).padStart(2, '0')}/${year}`,
            enable: true,
        });
        if (!confirmed) return;
        setBusy(true);
        try {
            await setWithholdingReporting(targets, true);
        } finally {
            setBusy(false);
        }
    };

    const openClient = async (row) => {
        setOpeningCaseId(row.caseId);
        try {
            const fullClient = await ClientService.GetClient(token, row.caseId);
            setDetailClient(fullClient || (clients || []).find((c) => c.caseId === row.caseId) || null);
        } finally {
            setOpeningCaseId(null);
        }
    };

    // ---------------------------------------------------------------- export

    const exportExcel = () => {
        try {
            const sectionText = (s) => {
                if (!s) return '';
                if (s.notRequired) return t('FollowUpOverviewNotRequired');
                const parts = [s.isReported ? t('FollowUpReported') : t('FollowUpNotReported')];
                if (s.reportDate) parts.push(formatShortDate(s.reportDate));
                if (s.followUpStatus) parts.push(statusLabel(s.followUpStatus));
                if (s.paymentMethod) parts.push(paymentLabel(s.paymentMethod));
                return parts.join(' · ');
            };
            const ws = buildReportSheet({
                titleLines: [
                    t('FollowUpOverviewTitle'),
                    `${getMonthName(month, language)} ${year}`,
                ],
                header: [
                    t('ClientName'),
                    t('CaseId'),
                    t('FollowUpVatReportingTypeColumn'),
                    t('FollowUpAdvanceReportingTypeColumn'),
                    ...SECTION_COLUMNS.map((c) => t(c.labelKey)),
                    t('FollowUpMaterialReceivedDate'),
                    t('FollowUpOutstandingFeeBalance'),
                ],
                rows: visibleRows.map((row) => [
                    row.name || '',
                    row.caseId,
                    reportingTypeLabel(row.reportingType, t),
                    reportingTypeLabel(row.advancedReportingType, t),
                    ...SECTION_COLUMNS.map(({ section }) => sectionText(sectionOf(row, section))),
                    formatShortDate(row.materialReceivedDate),
                    row.outstandingFeeBalance ?? '',
                ]),
                numericColIndexes: [SECTION_COLUMNS.length + 5],
            });
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, t('FollowUpOverviewSheet').slice(0, 31));
            applyWorkbookRtl(wb);
            XLSX.writeFile(wb, `${t('FollowUpOverviewTitle')} ${String(month).padStart(2, '0')}-${year}.xlsx`);
        } catch (error) {
            console.error('Error exporting reporting follow-up overview:', error);
            toast.error(t('ErrorGeneratingExcel'));
        }
    };

    // ---------------------------------------------------------------- render

    const renderSectionCell = (row, section) => {
        const s = sectionOf(row, section);
        if (!s) return <td key={section} className={styles.sectionCell} />;

        const isWithholding = section === FOLLOW_UP_SECTIONS.INCOME_TAX_WITHHOLDING;
        const tagClass = s.notRequired
            ? styles.notRequiredTag
            : s.isReported ? shared.reportedTag : shared.unreportedTag;
        const tagText = s.notRequired
            ? t('FollowUpOverviewNotRequired')
            : s.isReported ? t('FollowUpReported') : t('FollowUpNotReported');
        const details = [statusLabel(s.followUpStatus), paymentLabel(s.paymentMethod)].filter(Boolean);
        const dates = [
            s.reportDate ? `${t('FollowUpOverviewReportedOn')} ${formatShortDate(s.reportDate)}` : '',
            s.paymentDate ? `${t('FollowUpOverviewPaidOn')} ${formatShortDate(s.paymentDate)}` : '',
        ].filter(Boolean);

        return (
            <td key={section} className={styles.sectionCell} title={s.note || periodLabel(s, year)}>
                {isWithholding && !s.notRequired ? (
                    <button
                        type="button"
                        className={`${tagClass} ${styles.tagButton}`}
                        onClick={() => toggleRowWithholding(row)}
                        disabled={busy || togglingCaseId === row.caseId}
                        title={s.isReported ? t('FollowUpOverviewUnmarkWithholding') : t('FollowUpOverviewMarkWithholding')}
                    >
                        {togglingCaseId === row.caseId ? t('Loading') : tagText}
                    </button>
                ) : (
                    <span className={tagClass}>{tagText}</span>
                )}
                {s.isBiMonthly ? <span className={styles.periodHint}>{periodLabel(s, year)}</span> : null}
                {details.length ? <div className={styles.cellDetails}>{details.join(' · ')}</div> : null}
                {dates.length ? <div className={styles.cellDates}>{dates.join(' · ')}</div> : null}
            </td>
        );
    };

    const controlsDisabled = loading || busy;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            dir={dir}
            PaperProps={{
                className: shared.paper,
                sx: { maxWidth: 1440, width: 'min(1440px, calc(100vw - 32px))' },
            }}
            BackdropProps={{ sx: { background: 'rgba(20, 30, 43, 0.45)', backdropFilter: 'blur(6px)' } }}
        >
            <DialogTitle className={shared.header}>
                <span>{t('FollowUpOverviewTitle')}</span>
                <div className={shared.exportActions}>
                    <Button
                        className={shared.exportButton}
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={load}
                        disabled={controlsDisabled}
                    >
                        {t('Refresh')}
                    </Button>
                    <Button
                        className={shared.exportButton}
                        variant="outlined"
                        startIcon={<TableChartIcon />}
                        onClick={exportExcel}
                        disabled={controlsDisabled || visibleRows.length === 0}
                    >
                        {t('DownloadExcelFile')}
                    </Button>
                </div>
            </DialogTitle>

            <DialogContent className={shared.scroll}>
                <div className={styles.toolbar}>
                    <div className={shared.gridField}>
                        <span className={shared.fieldLabel}>{t('TaxYear')}</span>
                        <AutocompleteSelectFilter
                            options={yearOptions}
                            selectedValues={yearOptions.find((o) => o.value === year) || { value: year, label: String(year) }}
                            onChange={(e, option) => option && setYearMonth((prev) => ({ ...prev, year: option.value }))}
                            placeholder={t('TaxYear')}
                            isMultiple={false}
                            direction={dir}
                            portalMenu
                        />
                    </div>
                    <div className={shared.gridField}>
                        <span className={shared.fieldLabel}>{t('FollowUpReportMonth')}</span>
                        <AutocompleteSelectFilter
                            options={monthOptions}
                            selectedValues={monthOptions.find((o) => o.value === month) || null}
                            onChange={(e, option) => option && setYearMonth((prev) => ({ ...prev, month: option.value }))}
                            placeholder={t('FollowUpReportMonth')}
                            isMultiple={false}
                            direction={dir}
                            portalMenu
                        />
                    </div>
                    <div className={shared.gridField}>
                        <span className={shared.fieldLabel}>{t('FollowUpOverviewShow')}</span>
                        <AutocompleteSelectFilter
                            options={filterOptions}
                            selectedValues={filterOptions.find((o) => o.value === rowFilter) || filterOptions[0]}
                            onChange={(e, option) => setRowFilter(option?.value ?? ROW_FILTERS.ALL)}
                            placeholder={t('FollowUpOverviewShow')}
                            isMultiple={false}
                            direction={dir}
                            portalMenu
                        />
                    </div>
                    <label className={styles.search}>
                        <SearchIcon className={styles.searchIcon} />
                        <input
                            type="text"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder={t('searchClient')}
                            aria-label={t('searchClient')}
                        />
                    </label>
                </div>

                <div className={styles.summary}>
                    {summary.map(({ section, reported, total }) => (
                        <div key={section} className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>
                                {t(SECTION_COLUMNS.find((c) => c.section === section).labelKey)}
                            </span>
                            <span className={styles.summaryValue}>
                                {reported}/{total}
                            </span>
                        </div>
                    ))}
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>{t('FollowUpOverviewClients')}</span>
                        <span className={styles.summaryValue}>{visibleRows.length}</span>
                    </div>
                </div>

                {selectedRows.length > 0 ? (
                    <div className={styles.bulkBar}>
                        <span className={styles.bulkCount}>
                            {t('selectedClientsCount', { count: selectedRows.length })}
                        </span>
                        <div className={styles.bulkField}>
                            <AutocompleteSelectFilter
                                options={sectionOptions}
                                selectedValues={bulkSections}
                                onChange={(e, value) => setBulkSections(value || [])}
                                placeholder={t('FollowUpOverviewSections')}
                                isMultiple
                                direction={dir}
                                portalMenu
                            />
                        </div>
                        <div className={styles.bulkField}>
                            <AutocompleteSelectFilter
                                options={statusOptions}
                                selectedValues={bulkStatus}
                                onChange={(e, option) => setBulkStatus(option || null)}
                                placeholder={t('FollowUpStatus')}
                                isMultiple={false}
                                direction={dir}
                                portalMenu
                            />
                        </div>
                        <div className={styles.bulkField}>
                            <AutocompleteSelectFilter
                                options={paymentOptions}
                                selectedValues={bulkPayment}
                                onChange={(e, option) => setBulkPayment(option || null)}
                                placeholder={t('FollowUpPaymentMethod')}
                                isMultiple={false}
                                direction={dir}
                                portalMenu
                            />
                        </div>
                        <div className={styles.bulkDate} title={t('FollowUpReportDate')}>
                            <span className={shared.fieldLabel}>{t('FollowUpReportDate')}</span>
                            <FollowUpDatePicker
                                value={bulkReportDate}
                                onChange={setBulkReportDate}
                                ariaLabel={t('FollowUpReportDate')}
                            />
                        </div>
                        <div className={styles.bulkDate} title={t('FollowUpPaymentDate')}>
                            <span className={shared.fieldLabel}>{t('FollowUpPaymentDate')}</span>
                            <FollowUpDatePicker
                                value={bulkPaymentDate}
                                onChange={setBulkPaymentDate}
                                ariaLabel={t('FollowUpPaymentDate')}
                            />
                        </div>
                        <Button
                            className={shared.save}
                            variant="contained"
                            disableElevation
                            onClick={applyTracking}
                            disabled={!canApplyTracking || controlsDisabled}
                        >
                            {t('Apply')}
                        </Button>
                        <div className={styles.bulkActions}>
                            <Button
                                className={shared.reportButton}
                                variant="outlined"
                                onClick={markMaterialReceived}
                                disabled={controlsDisabled}
                            >
                                {t('MaterialReceivedAtOffice')}
                            </Button>
                            <Button
                                className={shared.reportButton}
                                variant="outlined"
                                onClick={reportSelectedWithholding}
                                disabled={controlsDisabled}
                            >
                                {t('FollowUpOverviewReportWithholding')}
                            </Button>
                            <Button
                                className={shared.reportButton}
                                variant="outlined"
                                onClick={() => setSelected(new Set())}
                                disabled={busy}
                            >
                                {t('clearSelection')}
                            </Button>
                        </div>
                    </div>
                ) : null}

                {loading && rows.length === 0 ? (
                    <div className={shared.loader}>
                        <PuffLoader size={80} loading />
                    </div>
                ) : (
                    <div className={styles.tableWrap} style={{ opacity: loading ? 0.5 : 1 }}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th className={styles.checkCell}>
                                        <Checkbox
                                            size="small"
                                            checked={allVisibleSelected}
                                            indeterminate={!allVisibleSelected && someVisibleSelected}
                                            onChange={toggleAllVisible}
                                            inputProps={{ 'aria-label': t('selectAll') }}
                                        />
                                    </th>
                                    <th className={styles.clientCell}>{t('ClientName')}</th>
                                    {SECTION_COLUMNS.map((c) => (
                                        <th key={c.section}>{t(c.labelKey)}</th>
                                    ))}
                                    <th>{t('FollowUpMaterialReceivedDate')}</th>
                                    <th>{t('FollowUpOutstandingFeeBalance')}</th>
                                    <th aria-label={t('FollowUpOverviewOpen')} />
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.map((row) => (
                                    <tr key={row.caseId} className={selected.has(row.caseId) ? styles.selectedRow : undefined}>
                                        <td className={styles.checkCell}>
                                            <Checkbox
                                                size="small"
                                                checked={selected.has(row.caseId)}
                                                onChange={() => toggleRow(row.caseId)}
                                                inputProps={{ 'aria-label': row.name || row.caseId }}
                                            />
                                        </td>
                                        <td className={styles.clientCell}>
                                            <div className={styles.clientName}>{row.name || '—'}</div>
                                            <div className={styles.clientCase}>{row.caseId}</div>
                                            {row.reportingType || row.advancedReportingType ? (
                                                <div className={styles.reportingTypes}>
                                                    {row.reportingType ? (
                                                        <span className={styles.reportingTypeBadge}>
                                                            {t('FollowUpVatReportingType', { type: reportingTypeLabel(row.reportingType, t) })}
                                                        </span>
                                                    ) : null}
                                                    {row.advancedReportingType ? (
                                                        <span className={styles.reportingTypeBadge}>
                                                            {t('FollowUpAdvanceReportingType', { type: reportingTypeLabel(row.advancedReportingType, t) })}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </td>
                                        {SECTION_COLUMNS.map(({ section }) => renderSectionCell(row, section))}
                                        <td className={styles.plainCell}>
                                            {row.materialReceivedDate
                                                ? <span className={shared.reportedTag}>{formatShortDate(row.materialReceivedDate)}</span>
                                                : '—'}
                                        </td>
                                        <td className={styles.amountCell}>
                                            {row.outstandingFeeBalance != null ? formatAmount(row.outstandingFeeBalance) : '—'}
                                        </td>
                                        <td className={styles.plainCell}>
                                            <Tooltip title={t('FollowUpOverviewOpen')}>
                                                <span>
                                                    <Button
                                                        className={shared.reportButton}
                                                        variant="outlined"
                                                        startIcon={<OpenInNewIcon />}
                                                        onClick={() => openClient(row)}
                                                        disabled={openingCaseId === row.caseId}
                                                    >
                                                        {openingCaseId === row.caseId ? t('Loading') : t('FollowUpOverviewOpen')}
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </td>
                                    </tr>
                                ))}
                                {visibleRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={SECTION_COLUMNS.length + 5} className={styles.emptyRow}>
                                            {t('FollowUpOverviewNoClients')}
                                        </td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                )}
            </DialogContent>

            <DialogActions className={shared.actions}>
                <Button className={shared.cancel} variant="contained" disableElevation onClick={onClose}>
                    {t('close')}
                </Button>
            </DialogActions>

            <ReportingFollowUpModal
                open={!!detailClient}
                onClose={() => setDetailClient(null)}
                client={detailClient}
                user={user}
                token={token}
                defaultDate={new Date(year, month - 1, 1)}
            />
        </Dialog>
    );
};

ReportingFollowUpOverviewModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    clients: PropTypes.arrayOf(PropTypes.shape({ caseId: PropTypes.string, name: PropTypes.string })),
    user: PropTypes.object,
    token: PropTypes.string,
    defaultDate: PropTypes.instanceOf(Date),
    onWithholdingReportingChange: PropTypes.func,
};

export default ReportingFollowUpOverviewModal;
